"use client";

import React, { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { PostHogProvider } from "./PostHogProvider";
import appConfig from "../app.config";
import type { LogLevel } from "../utils/logLevel";

export type MonitoringEventType =
  | "ui_event"
  | "fetch_request"
  | "system_event"
  | "lsa_event"
  | "user_action"
  | "breadcrumb"
  | "log_message";

const CLIENT_LEVEL_ORDER: Record<string, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const CONSOLE_METHOD: Record<LogLevel, "debug" | "log" | "warn" | "error"> = {
  debug: "debug",
  info: "log",
  warn: "warn",
  error: "error",
};

export interface AnalyticsContextType {
  registerUser: (_userID: string, _userEmail: string) => void;
  clearUser: () => void;
  triggerErrorReport: (_error: Error) => void;
  logEvent: (_eventType: MonitoringEventType, _eventName: string, _data?: Record<string, unknown>) => void;
  logBreadcrumb: (_breadcrumbName: string, _data?: Record<string, unknown>) => void;
  logMessage: (_level: LogLevel, _message: string, _data?: Record<string, unknown>) => void;
  logError: (_message: string, _error: Error, _data?: Record<string, unknown>) => void;
}

// Exported so Storybook / design-sync providers can supply a no-op value without PostHog
export const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderInnerProps {
  children: ReactNode;
  isEnabled: boolean;
}

function AnalyticsProviderInner({ children, isEnabled }: AnalyticsProviderInnerProps) {
  const posthog = usePostHog();
  const [minLogLevel, setMinLogLevel] = useState<string | undefined>(undefined);

  const environmentFields = {
    environment: appConfig.analytics?.environment ?? "",
    version: appConfig.version ?? "",
  };

  useEffect(() => {
    if (!isEnabled) return;
    posthog.onFeatureFlags(() => {
      const level = posthog.getFeatureFlag("client-log-level");
      if (typeof level === "string") setMinLogLevel(level);
    });
  }, [isEnabled, posthog]);

  const registerUser = (userID: string, userEmail: string) => {
    if (!isEnabled) return;
    posthog.identify(userID, { email: userEmail });
  };

  const clearUser = () => {
    if (!isEnabled) return;
    posthog.reset();
  };

  const triggerErrorReport = (error: Error) => {
    if (!isEnabled) return;
    posthog.captureException(error, { ...environmentFields });
  };

  const logEvent = (eventType: MonitoringEventType, eventName: string, data?: Record<string, unknown>) => {
    if (!isEnabled) return;
    posthog.capture(eventName, { eventType: eventType.toString(), ...data, ...environmentFields });
  };

  const logBreadcrumb = (breadcrumbName: string, data?: Record<string, unknown>) => {
    if (!isEnabled) return;
    logEvent("breadcrumb", breadcrumbName, data);
  };

  const logMessage = (level: LogLevel, message: string, data?: Record<string, unknown>) => {
    if (!isEnabled) {
      console[CONSOLE_METHOD[level]](message, data);
      return;
    }
    console[CONSOLE_METHOD[level]](message, data);
    if (minLogLevel === "off") return;
    if (minLogLevel !== undefined) {
      const minOrder = CLIENT_LEVEL_ORDER[minLogLevel] ?? CLIENT_LEVEL_ORDER.warn;
      if ((CLIENT_LEVEL_ORDER[level] ?? 0) < minOrder) return;
    }
    logEvent("log_message", message, { severity: level, ...data });
  };

  const logError = (message: string, error: Error, data?: Record<string, unknown>) => {
    if (!isEnabled) {
      console.error(message, error, data);
      return;
    }
    console.error(message, error, data);
    logEvent("log_message", message, {
      severity: "error",
      "error.message": error.message,
      "error.stack": error.stack,
      ...data,
    });
    triggerErrorReport(error);
  };

  return (
    <AnalyticsContext.Provider
      value={{ registerUser, clearUser, triggerErrorReport, logEvent, logBreadcrumb, logMessage, logError }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

interface AnalyticsProviderProps {
  children: ReactNode;
  isEnabled: boolean;
}

export function AnalyticsProvider({ children, isEnabled }: AnalyticsProviderProps) {
  return (
    <PostHogProvider isEnabled={isEnabled}>
      <AnalyticsProviderInner isEnabled={isEnabled}>{children}</AnalyticsProviderInner>
    </PostHogProvider>
  );
}

export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};
